import { useMutation, useQuery } from "@tanstack/react-query";
import {
  NOTIFICATIONS_KEY,
  useInboxRefresh,
} from "@/hooks/use-notifications-refresh";
import { trackNotifications } from "@/features/notifications/analytics";
import { useUserEvents } from "@/hooks/use-room-events";
import { useVisiblePolling } from "@/hooks/use-visible-polling";
import {
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/lib/client/notifications";
import type { ReadAllInput } from "@/lib/autar/notifications";

export const NOTIFICATION_POLL_MS = 60_000;
const BELL_ITEMS = 8;

/**
 * The bell's data: the unread count and the latest items. The server sends
 * invalidation-only events, so each one just refetches; polling is the
 * fallback for a dropped socket and stops while the tab is hidden.
 */
export function useNotifications() {
  const count = useQuery({
    queryKey: [...NOTIFICATIONS_KEY, "count"],
    queryFn: ({ signal }) => getUnreadCount(signal),
  });
  const latest = useQuery({
    queryKey: [...NOTIFICATIONS_KEY, "latest"],
    queryFn: ({ signal }) => listNotifications({ limit: BELL_ITEMS }, signal),
  });

  const refresh = useInboxRefresh();
  useUserEvents(
    { "notification.created": refresh, "notifications.changed": refresh },
    refresh,
  );
  useVisiblePolling(refresh, { intervalMs: NOTIFICATION_POLL_MS });

  const read = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: () => trackNotifications("bell", "mark_read"),
    onSettled: refresh,
  });
  const readAll = useMutation({
    mutationFn: (input: ReadAllInput) => markAllNotificationsRead(input),
    onSuccess: () => trackNotifications("bell", "mark_all_read"),
    onSettled: refresh,
  });

  const items = latest.data?.items ?? [];
  return {
    unread: count.data ?? 0,
    unreadKnown: count.data !== undefined,
    items,
    loading: count.isPending || latest.isPending,
    // A failed read is an error, never an empty inbox.
    error: count.error ?? latest.error ?? null,
    refresh,
    markRead: (id: string) => read.mutateAsync(id),
    markAllRead: () => {
      const newest = items[0];
      return newest
        ? readAll.mutateAsync({ upToId: newest.id })
        : Promise.resolve(undefined);
    },
  };
}
